/**
 * useLiveQuotes - 2026-06-07 settings-emergency-quotes-refresh-final.
 *
 * Canonical hook for fetching live quotes from the
 * /api/v1/market-data/quotes endpoint.
 *
 *   const { quotes, loading, error, refresh } =
 *     useLiveQuotes(["AAPL", "META", "COIN"]);
 *
 * Each quotes row has the canonical shape:
 *   { symbol, price, bid, ask, change, change_pct, volume,
 *     source, quote_time_utc, is_stale, stale_reason, data_quality }
 *
 * Never invents numbers - missing data surfaces as data_quality
 * "unavailable" with all numeric fields null.
 *
 * Subscribes to `adversyn:refresh:market-pulse` so any market-pulse
 * refresh button on the page also refreshes the quotes hook.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiV1Base } from "./apiConfig";
import { subscribeRefresh } from "./refreshEvents";

export interface LiveQuote {
  symbol: string;
  price: number | null;
  bid: number | null;
  ask: number | null;
  change: number | null;
  change_pct: number | null;
  volume: number | null;
  source: string;
  quote_time_utc: string | null;
  is_stale: boolean;
  stale_reason: string | null;
  data_quality: "live" | "stale" | "after_hours" | "closed" | "unavailable";
}

export interface UseLiveQuotesResult {
  quotes: Record<string, LiveQuote>;
  loading: boolean;
  error: string | null;
  lastUpdatedAt: number | null;
  refresh: () => Promise<void>;
}

export default function useLiveQuotes(symbols: string[]): UseLiveQuotesResult {
  const key = useMemo(
    () =>
      Array.from(
        new Set(
          (symbols || [])
            .map((s) => String(s).trim().toUpperCase())
            .filter(Boolean),
        ),
      )
        .sort()
        .join(","),
    [symbols],
  );

  const [quotes, setQuotes] = useState<Record<string, LiveQuote>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const cancelRef = useRef<boolean>(false);

  const refresh = useCallback(async () => {
    if (!key) {
      setQuotes({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    cancelRef.current = false;
    try {
      const url = apiV1Base() + "/market-data/quotes?symbols=" + encodeURIComponent(key);
      const r = await fetch(url, { cache: "no-store" });
      if (cancelRef.current) return;
      if (!r.ok) {
        setError("HTTP " + r.status);
        setLoading(false);
        return;
      }
      const body = await r.json();
      const data = body?.data ?? body;
      const rows: LiveQuote[] = Array.isArray(data?.quotes) ? data.quotes : [];
      const out: Record<string, LiveQuote> = {};
      for (const row of rows) {
        if (row && typeof row.symbol === "string") {
          out[row.symbol] = row;
        }
      }
      setQuotes(out);
      setLastUpdatedAt(Date.now());
    } catch (e: any) {
      if (cancelRef.current) return;
      setError(String(e?.message || e));
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    cancelRef.current = false;
    void refresh();
    return () => { cancelRef.current = true; };
  }, [refresh]);

  useEffect(() => {
    const off = subscribeRefresh("market-pulse", () => { void refresh(); });
    return () => { off(); };
  }, [refresh]);

  return { quotes, loading, error, lastUpdatedAt, refresh };
}
